import { z } from 'zod';
import { stockResearchDocumentSchema } from './research.js';
export * from './research.js';
export const ACCOUNT_TERMS_VERSION = '2026-08-29';
export const ACCOUNT_PRIVACY_VERSION = '2026-08-29';
export const ACCOUNT_RISK_DISCLOSURE_VERSION = '2026-08-29';
export const accountUsernamePattern = /^[a-zA-Z][a-zA-Z0-9_]{3,31}$/;
export const accountPhonePattern = /^1[3-9]\d{9}$/;
export function accountPasswordIssue(value) {
    if (value.length < 8 || value.length > 128)
        return 'password_length_invalid';
    if (!/[a-zA-Z]/.test(value) || !/\d/.test(value))
        return 'password_requires_letter_and_number';
    return null;
}
export const accountPasswordSchema = z.string().superRefine((value, ctx) => {
    const issue = accountPasswordIssue(value);
    if (issue)
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: issue });
});
export const accountRegistrationSchema = z.object({
    username: z.string().trim().regex(accountUsernamePattern).transform((value) => value.toLowerCase()),
    password: accountPasswordSchema,
    phone: z.string().trim().regex(accountPhonePattern),
    acceptedLegalTerms: z.literal(true),
    termsVersion: z.literal(ACCOUNT_TERMS_VERSION),
    privacyVersion: z.literal(ACCOUNT_PRIVACY_VERSION),
    riskDisclosureVersion: z.literal(ACCOUNT_RISK_DISCLOSURE_VERSION)
});
export const userRoleSchema = z.enum(['member', 'analyst', 'admin']);
export const analysisModeSchema = z.enum(['historical_strict', 'live_research', 'ad_hoc_research']);
export const conclusionSchema = z.enum(['favorable', 'neutral', 'avoid']);
export const candidateSchema = z.object({
    rank: z.number().int().positive(),
    code: z.string().regex(/^\d{6}$/),
    name: z.string().min(1),
    industry: z.string().default('未分类'),
    strategy: z.string().default('综合量化'),
    price: z.number().positive().nullable().default(null),
    changePct: z.number().nullable().default(null),
    quantScore: z.number().min(0).max(100),
    factorScore: z.number().min(0).max(100).nullable().default(null),
    riskLevel: z.number().int().min(1).max(5).nullable().default(null),
    riskTag: z.string().default('未知'),
    reason: z.string().default(''),
    signalDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    quoteTradeDate: z.string().regex(/^\d{8}$/),
    qualityTier: z.string().default('research'),
    modelStatus: z.enum(['no_champion', 'champion']).default('no_champion'),
    modelT5ExcessProbability: z.number().min(0).max(1).nullable().default(null),
    /** @deprecated Compatibility alias. This is also an excess-return probability, not absolute upside. */
    modelT5Probability: z.number().min(0).max(1).nullable().default(null),
    portfolioSelected: z.boolean().default(false),
    sourceRecord: z.record(z.unknown()).default({})
});
export const validationSummarySchema = z.object({
    schemaVersion: z.literal('validation-summary-v1'),
    available: z.boolean(),
    generatedAt: z.string().datetime({ offset: true }).nullable(),
    primaryHorizon: z.string().min(1),
    primaryMetric: z.string().min(1),
    promotionStatus: z.string().min(1),
    promotionGates: z.record(z.boolean()),
    sampleCount: z.number().int().nonnegative(),
    signalDateCount: z.number().int().nonnegative(),
    sampleWinRatePct: z.number().min(0).max(100).nullable(),
    sampleExcessWinRatePct: z.number().min(0).max(100).nullable(),
    sampleMeanNetPct: z.number().nullable().optional(),
    dateMeanNetPct: z.number().nullable().optional(),
    dateMeanExcessPct: z.number().nullable(),
    ci95Lower: z.number().nullable(),
    ci95Upper: z.number().nullable(),
    netCi95Lower: z.number().nullable().optional(),
    netCi95Upper: z.number().nullable().optional(),
    netProfitFactor: z.number().nonnegative().nullable().optional(),
    maxDrawdownPct: z.number().nonnegative().nullable().optional(),
    brokerSignalDateCount: z.number().int().nonnegative().optional(),
    recentDateCount: z.number().int().nonnegative(),
    recentMeanNetPct: z.number().nullable().optional(),
    recentMeanExcessPct: z.number().nullable(),
    pbo: z.number().min(0).max(1).nullable()
});
export const quantSnapshotSchema = z.object({
    schemaVersion: z.literal('quant-snapshot-v1'),
    snapshotId: z.string().min(12),
    sourceSchemaVersion: z.string().min(1),
    signalDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    signalTime: z.string().regex(/^\d{2}:\d{2}:\d{2}$/),
    decisionAt: z.string().datetime({ offset: true }),
    quoteTradeDate: z.string().regex(/^\d{8}$/),
    stage: z.enum(['scored_universe', 'final_selection']),
    configVersion: z.union([z.string(), z.number()]).transform(String),
    configSha256: z.string().regex(/^[a-f0-9]{64}$/),
    recordsSha256: z.string().regex(/^[a-f0-9]{64}$/),
    sourceSnapshotId: z.string().min(12).nullable().optional(),
    sourceRecordsSha256: z.string().regex(/^[a-f0-9]{64}$/).nullable().optional(),
    selectionCount: z.number().int().positive().optional(),
    productionReady: z.boolean(),
    promotionStatus: z.string(),
    validation: validationSummarySchema,
    candidates: z.array(candidateSchema).min(1),
    generatedAt: z.string().datetime({ offset: true })
}).superRefine((snapshot, ctx) => {
    const mismatched = snapshot.candidates.find((row) => row.quoteTradeDate !== snapshot.quoteTradeDate);
    if (mismatched) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `candidate ${mismatched.code} has a mixed quote trade date`,
            path: ['candidates']
        });
    }
    const invalidProbability = snapshot.candidates.find((row) => (row.modelT5ExcessProbability !== null || row.modelT5Probability !== null) && row.modelStatus !== 'champion');
    if (invalidProbability) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `candidate ${invalidProbability.code} exposes an unvalidated probability`,
            path: ['candidates']
        });
    }
    const inconsistentProbability = snapshot.candidates.find((row) => row.modelT5ExcessProbability !== null
        && row.modelT5Probability !== null
        && Math.abs(row.modelT5ExcessProbability - row.modelT5Probability) > 1e-12);
    if (inconsistentProbability) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `candidate ${inconsistentProbability.code} has inconsistent probability aliases`,
            path: ['candidates']
        });
    }
    if (snapshot.productionReady !== (snapshot.promotionStatus === 'ready')) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'production readiness disagrees with promotion status',
            path: ['productionReady']
        });
    }
    if (snapshot.validation.promotionStatus !== snapshot.promotionStatus) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'snapshot and validation promotion statuses disagree',
            path: ['validation', 'promotionStatus']
        });
    }
    if (snapshot.productionReady && !snapshot.candidates.some((row) => row.modelStatus === 'champion')) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'production-ready snapshot requires a champion model',
            path: ['candidates']
        });
    }
});
export const evidenceItemSchema = z.object({
    id: z.string().min(1),
    type: z.enum(['quant', 'market', 'fundamental', 'announcement', 'news', 'social', 'knowledge']),
    title: z.string().min(1),
    sourceName: z.string().min(1),
    sourceUrl: z.string().url().nullable().default(null),
    publishedAt: z.string().datetime({ offset: true }).nullable().default(null),
    availableAt: z.string().datetime({ offset: true }),
    contentHash: z.string().regex(/^[a-f0-9]{64}$/),
    sourceContentHash: z.string().regex(/^[a-f0-9]{64}$/).nullable().optional(),
    sourceAvailableAt: z.string().datetime({ offset: true }).nullable().optional(),
    excerpt: z.string().max(1200)
});
export const knowledgeLicenseStatusSchema = z.enum(['approved', 'internal_only', 'blocked']);
const knowledgeDocumentBaseSchema = z.object({
    id: z.string().uuid(),
    sourceType: z.string().min(1).max(80),
    sourceName: z.string().min(1).max(160),
    sourceUrl: z.string().url().nullable(),
    title: z.string().min(1).max(300),
    publishedAt: z.string().datetime({ offset: true }).nullable(),
    availableAt: z.string().datetime({ offset: true }),
    effectiveFrom: z.string().datetime({ offset: true }).nullable(),
    effectiveTo: z.string().datetime({ offset: true }).nullable(),
    contentHash: z.string().regex(/^[a-f0-9]{64}$/),
    content: z.string().min(1).max(100_000),
    licenseStatus: knowledgeLicenseStatusSchema,
    humanReviewed: z.boolean(),
    createdAt: z.string().datetime({ offset: true })
});
function validateKnowledgeTimeline(document, ctx) {
    if (document.publishedAt && Date.parse(document.publishedAt) > Date.parse(document.availableAt)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'publishedAt cannot be after availableAt', path: ['publishedAt'] });
    }
    if (document.effectiveFrom && document.effectiveTo && Date.parse(document.effectiveFrom) > Date.parse(document.effectiveTo)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'effectiveFrom cannot be after effectiveTo', path: ['effectiveFrom'] });
    }
}
export const knowledgeDocumentSchema = knowledgeDocumentBaseSchema.superRefine(validateKnowledgeTimeline);
export const createKnowledgeDocumentSchema = knowledgeDocumentBaseSchema.omit({
    id: true,
    contentHash: true,
    createdAt: true
}).superRefine(validateKnowledgeTimeline);
export const evidenceBundleSchema = z.object({
    schemaVersion: z.literal('evidence-bundle-v1'),
    snapshotId: z.string().min(12).nullable(),
    stockCode: z.string().regex(/^\d{6}$/),
    stockName: z.string().min(1),
    mode: analysisModeSchema,
    asOf: z.string().datetime({ offset: true }),
    candidate: candidateSchema.nullable(),
    researchDocument: stockResearchDocumentSchema.nullable().optional(),
    evidence: z.array(evidenceItemSchema),
    missingSources: z.array(z.string()).default([]),
    bundleHash: z.string().regex(/^[a-f0-9]{64}$/)
});
const analystSectionSchema = z.object({
    status: z.enum(['complete', 'insufficient_evidence', 'unavailable']),
    summary: z.string(),
    positiveEvidenceIds: z.array(z.string()),
    negativeEvidenceIds: z.array(z.string()),
    cautions: z.array(z.string())
});
export const agentReportSchema = z.object({
    schemaVersion: z.literal('agent-report-v1'),
    reportId: z.string().uuid(),
    jobId: z.string().uuid(),
    snapshotId: z.string().min(12).nullable(),
    researchDocumentId: z.string().regex(/^[a-f0-9]{64}$/).nullable().optional(),
    cacheExpiresAt: z.string().datetime({ offset: true }).nullable().optional(),
    analysisVersion: z.string().min(1).max(120).nullable().optional(),
    stockCode: z.string().regex(/^\d{6}$/),
    stockName: z.string().min(1),
    mode: analysisModeSchema,
    asOf: z.string().datetime({ offset: true }),
    conclusion: conclusionSchema,
    evidenceIntegrity: z.enum(['complete', 'partial', 'failed']),
    sections: z.object({
        market: analystSectionSchema,
        fundamentals: analystSectionSchema,
        news: analystSectionSchema,
        social: analystSectionSchema
    }),
    bullCase: z.string(),
    bearCase: z.string(),
    riskAssessment: z.string(),
    riskFlags: z.array(z.string()),
    evidence: z.array(evidenceItemSchema),
    model: z.object({
        engine: z.string(),
        provider: z.string(),
        quickModel: z.string(),
        deepModel: z.string(),
        promptHash: z.string().regex(/^[a-f0-9]{64}$/),
        upstreamCommit: z.string().nullable().default(null)
    }),
    usage: z.object({
        promptTokens: z.number().int().nonnegative().nullable(),
        completionTokens: z.number().int().nonnegative().nullable(),
        usageStatus: z.enum(['provider_reported', 'not_applicable', 'unavailable']).optional(),
        llmCalls: z.number().int().nonnegative().optional(),
        latencyMs: z.number().int().nonnegative()
    }),
    generatedAt: z.string().datetime({ offset: true })
});
export const analysisJobSchema = z.object({
    id: z.string().uuid(),
    snapshotId: z.string().min(12).nullable(),
    researchDocumentId: z.string().regex(/^[a-f0-9]{64}$/).nullable().optional(),
    cacheExpiresAt: z.string().datetime({ offset: true }).nullable().optional(),
    analysisVersion: z.string().min(1).max(120).nullable().optional(),
    stockCode: z.string().regex(/^\d{6}$/),
    stockName: z.string().min(1),
    mode: analysisModeSchema,
    requestedBy: z.string(),
    status: z.enum(['queued', 'leased', 'running', 'completed', 'failed', 'cancelled']),
    progress: z.number().int().min(0).max(100),
    stage: z.string(),
    attempt: z.number().int().nonnegative(),
    leaseExpiresAt: z.string().datetime({ offset: true }).nullable(),
    errorCode: z.string().nullable(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true })
});
export const createAnalysisSchema = z.object({
    stockCode: z.string().regex(/^\d{6}$/),
    snapshotId: z.string().min(12).optional(),
    mode: analysisModeSchema.default('live_research')
});
export const createBatchAnalysisSchema = z.object({
    stockCodes: z.array(z.string().regex(/^\d{6}$/)).min(1).max(10),
    snapshotId: z.string().min(12).optional(),
    mode: analysisModeSchema.default('live_research')
});
export const paperOrderSchema = z.object({
    accountId: z.string().uuid(),
    stockCode: z.string().regex(/^\d{6}$/),
    side: z.enum(['buy', 'sell']),
    quantity: z.number().int().positive().refine((value) => value % 100 === 0, 'A-share orders must use board lots of 100'),
    orderType: z.literal('next_open'),
    signalSnapshotId: z.string().min(12)
});
export const publicDashboardSchema = z.object({
    snapshot: quantSnapshotSchema.nullable(),
    snapshotSyncedAt: z.string().datetime({ offset: true }).nullable(),
    validation: validationSummarySchema.nullable(),
    worker: z.object({
        online: z.boolean(),
        lastHeartbeatAt: z.string().datetime({ offset: true }).nullable(),
        queuedJobs: z.number().int().nonnegative()
    }),
    // Response creation time. Data freshness is represented by snapshotSyncedAt.
    generatedAt: z.string().datetime({ offset: true })
});
